// src/pages/modules/CategoryPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  clearError,
  exportCategories,
  importPreview,
  importConfirm,
} from '../../store/slices/categorySlice';
import { Pencil, Trash2, MoreHorizontal, Loader2, MoreVertical } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PageHeader from '../../components/common/PageHeader';
import FormField from '../../components/common/FormField';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import { formatDateTime } from 'utlis/formateDateTime';
import { restoreRole } from 'store/slices/commonSlice';

const CategoryPage = () => {
  usePermissionSocket();

  const dispatch = useDispatch();
  const { categories, pagination, isLoading, error } = useSelector((s) => s.category);
  const { canCreate, canUpdate, canDelete, canExport, canImport, canRead } = usePermissions('category');

  // Pagination & Search
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialogs
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', is_canteen: false, is_it: false, is_active: true });
  const [formErrors, setFormErrors] = useState({});

  // Reset page to 1 when searching
  useEffect(() => {
    const timer = setTimeout(() => setPage(1), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Refetch with search
  const refetch = useCallback(() => {
    if (canRead) {
      dispatch(fetchCategories({ page, pageSize, search: searchTerm }));
    }
  }, [dispatch, page, pageSize, searchTerm, canRead]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'Operation failed');
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    else if (formData.name.trim().length > 25) errors.name = 'Max 25 characters';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openDialog = (cat = null) => {
    if (cat && !canUpdate) return toast.error("No edit permission");
    if (!cat && !canCreate) return toast.error("No create permission");

    setCurrentCategory(cat);
    setFormData(cat ? {
      name: cat.name || '',
      is_canteen: !!cat.is_canteen,
      is_it: !!cat.is_it,
      is_active: cat.is_active !== false,
    } : { name: '', is_canteen: false, is_it: false, is_active: true });
    setFormErrors({});
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (currentCategory) {
        await dispatch(updateCategory({ id: currentCategory.id, ...formData })).unwrap();
        toast.success('Category updated');
      } else {
        await dispatch(createCategory(formData)).unwrap();
        toast.success('Category created');
      }
      setIsDialogOpen(false);
      refetch();
    } catch (err) {
      const errorMessage =
        typeof err === 'string'
          ? err
          : err?.message || 'Failed to create category';

      toast.error(errorMessage);
    }

  };

  const handleDelete = async () => {
    if (!canDelete) return toast.error("No delete permission");
    await dispatch(deleteCategory(currentCategory.id)).unwrap();
    toast.success('Category deleted');
    setIsDeleteDialogOpen(false);
    refetch();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    if (formErrors[name]) setFormErrors(p => ({ ...p, [name]: '' }));
  };

  const columns = [
    { accessorKey: 'name', header: 'Category Name', cell: ({ row }) => <span className="font-medium">{row.original.name}</span> },
    {
      header: 'Type',
      cell: ({ row }) => {
        const c = row.original;
        return (
          <div className="flex flex-wrap gap-1">
            {c.is_canteen && <Badge variant="outline">Canteen</Badge>}
            {c.is_it && <Badge variant="outline">IT</Badge>}
            {!c.is_canteen && !c.is_it && <Badge variant="outline">General</Badge>}
          </div>
        );
      },
    },
    { accessorKey: 'is_active', header: 'Status', cell: ({ row }) => <Badge variant={row.original.is_active ? 'success' : 'secondary'}>{row.original.is_active ? 'Active' : 'Inactive'}</Badge> },
    {
      accessorKey: 'created_at',
      header: 'Created On',
      cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.created_at) ?? '-'}</span>,
    },
    {
      accessorKey: 'created_by_name',
      header: 'Created By',
      cell: ({ row }) => <span className="text-sm">{row.original.created_by_name ?? '-'}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canUpdate && <DropdownMenuItem onClick={() => openDialog(row.original)}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>}
            {canDelete && <DropdownMenuItem onClick={() => { setCurrentCategory(row.original); setIsDeleteDialogOpen(true); }} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="Categories" description="Manage inventory & canteen categories" action={canCreate ? { label: 'Add Category', onClick: () => openDialog() } : null} />

      {/* {isLoading && !categories.length && <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin mr-3" /> Loading...</div>} */}

      {!canRead && !isLoading && <div className="text-center py-12 text-muted-foreground">No permission to view categories</div>}

      {canRead && (
        <DataTable
          data={categories}
          totalCount={pagination.count}
          columns={columns}
          templateUrl="/import-template/category.csv"
          templateDownloadName="category-import-template.csv"
          templateKey="category"
          searchPlaceholder="Search categories..."
          showExport={canExport}
          onExport={canExport ? () => dispatch(exportCategories()) : undefined}
          showImport={canImport}
          onImportPreview={canImport ? (f) => dispatch(importPreview(f)).unwrap() : undefined}
          onImportConfirm={canImport ? (p) => dispatch(importConfirm(p)).unwrap().then(refetch) : undefined}
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={pagination.count ? Math.ceil(pagination.count / pagination.page_size) : 1}
          onPageChange={(i) => setPage(i + 1)}
          onPageSizeChange={setPageSize}
          onSearch={setSearchTerm}
          isLoading={isLoading}
          onDelete={async (id) => {
            await dispatch(deleteCategory(id));
            refetch();
          }}
          onRestore={async (id) => {
            await dispatch(restoreRole({ entityType: 'categories', id }));
            refetch();
          }}
        />
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{currentCategory ? 'Edit' : 'Add'} Category</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField label="Name" name="name" value={formData.name} onChange={handleChange} error={formErrors.name} required maxLength={25} placeholder="e.g. Snacks" />
            <div className="space-y-3">
              <FormField label="Canteen" name="is_canteen" type="switch" checked={formData.is_canteen} onCheckedChange={(c) => setFormData(p => ({ ...p, is_canteen: c }))} />
              <FormField label="IT" name="is_it" type="switch" checked={formData.is_it} onCheckedChange={(c) => setFormData(p => ({ ...p, is_it: c }))} />
              <FormField label="Active" name="is_active" type="switch" checked={formData.is_active} onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))} />
            </div>
            <p className="text-sm text-red-600 bg-red-50 p-2 rounded-md">
              A category can be created for either <strong>Canteen</strong> or <strong>IT</strong>.
              Selecting both at the same time is not allowed.
            </p>


            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={isLoading}>{currentCategory ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Category?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogDescription>Delete <strong>{currentCategory?.name}</strong>? This cannot be undone.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoryPage;